<script setup lang="ts">
import { computed, inject, onMounted, reactive, ref, watch } from 'vue';
import Pinyin from 'js-pinyin';
import { langListMap } from '@/constants/langList';
import { formatGroupedList } from '@/utils/index';
import { fetchGetSetLang, fetchPostSetLang } from '@/service/api';
import { $t } from '@/locales';
Pinyin.setOptions({ checkPolyphone: false, charCase: 0 });
const emit = defineEmits(['updateLangList']);
const show = ref(false);
const needUpdate = ref(0);
const selectLangList = ref<string[]>([]);
const openModel = (need: number) => {
  show.value = true;
  needUpdate.value = need;
};
const getLangList = async () => {
  const { data } = await fetchGetSetLang();
  selectLangList.value = data.codes ? data.codes : ['zh_CN', 'en'];
};
watch(show, newVal => {
  if (newVal) {
    getLangList();
  }
});
const handleClose = (code: string) => {
  selectLangList.value = selectLangList.value.filter(i => i !== code);
};
const closeModel = () => {
  show.value = false;
};
const callGetLangList = inject<() => void>('callGetLangList');
const confirmUpdate = async () => {
  const { error } = await fetchPostSetLang({ languages: selectLangList.value });
  if (!error) {
    window.$message?.success($t('common.modifySuccess'));
    if (needUpdate.value === 1) {
      if (callGetLangList) {
        callGetLangList();
      }
    }
    closeModel();
  }
};
const groupedList = computed(() => {
  return formatGroupedList(langListMap);
});
const selectLangItem = (code: string) => {
  if (selectLangList.value.length >= 20) {
    window.$message.warning($t('mutilLang.tip5'));
    return;
  }
  if (code === 'zh_CN' || code === 'en') {
    window.$message.warning($t('mutilLang.tip6'));
    return;
  }
  if (selectLangList.value.includes(code)) {
    selectLangList.value = selectLangList.value.filter(i => i !== code);
  } else {
    selectLangList.value.push(code);
  }
};
onMounted(() => {
  getLangList();
});

defineExpose({
  openModel
});
</script>

<template>
  <NModal
    v-model:show="show"
    :segmented="{
      content: true,
      footer: 'soft'
    }"
    :bordered="false"
    class="custom-card w-1/3"
    preset="card"
    :title="$t('mutilLang.langManger')"
    :mask-closable="false"
  >
    <NSpace vertical>
      <NGrid responsive="screen">
        <NGridItem :span="1"><icon-local-info class="text-icon" /></NGridItem>
        <NGridItem :span="18">
          <span>{{ $t('mutilLang.tip4') }}</span>
        </NGridItem>
      </NGrid>
      <div>{{ $t('mutilLang.selectedLang') }}</div>
      <NSpace>
        <NTag
          v-for="code in selectLangList"
          :key="code"
          :closable="code !== 'en' && code !== 'zh_CN'"
          size="small"
          round
          @close="handleClose(code)"
        >
          {{ $t(langListMap[code]) }}
        </NTag>
      </NSpace>
      <div>{{ $t('mutilLang.all') }}</div>
      <NScrollbar class="max-h-400px" trigger="none">
        <div class="alphabet-grid">
          <div v-for="(group, index) in groupedList" :key="group.letter" class="group">
            <h3>{{ group.letter }}</h3>
            <div class="lang-box">
              <div
                v-for="item in group.items"
                :key="item.code"
                class="lang-txt"
                :class="selectLangList.includes(item.code) ? 'selected' : ''"
                @click="selectLangItem(item.code)"
              >
                {{ $t(langListMap[item.code]) }}
              </div>
            </div>
          </div>
        </div>
      </NScrollbar>
    </NSpace>
    <template #footer>
      <NSpace justify="end">
        <NButton type="default" class="w-96px" @click="closeModel">{{ $t('common.cancel') }}</NButton>
        <NButton type="primary" class="ml-2 min-w-96px" @click="confirmUpdate">
          {{ $t('common.ensure') }}
        </NButton>
      </NSpace>
    </template>
  </NModal>
</template>

<style lang="scss" scoped>
.alphabet-grid {
  // display: grid;
  // grid-template-columns: repeat(4, 1fr);
  // gap: 10px;
  column-count: 3;
  column-gap: 30px;
}
.group {
  text-align: left;
  break-inside: avoid;
  margin-bottom: 20px;
}
h3 {
  margin: 0;
  color: #666;
  font-weight: bold;
}
.lang-box {
  list-style: none;
  padding: 0;
}
.lang-txt {
  margin: 5px 0;
  cursor: pointer;
}
.lang-txt:hover {
  color: #409eff;
}
.selected {
  color: #999;
}
</style>
